import { useEffect, useState } from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import SelectSearchInput from "../SelectSearch";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import EmployeeSalesBarChart from "./EmployeeSalesBarChart";
import EmployeeTable from "./EmployeeTable";
import ItemSoldTable from "./ItemSoldTable";

const EmployeeAnalysis = () => {
    const today = new Date().toISOString().split('T')[0];

    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [token, setToken] = useState("");
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [loading, setLoading] = useState(false);
    const [employeeData, setEmployeeData] = useState([]);

    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_BASE_URL;

    useEffect(() => {
        const storedToken = sessionStorage.getItem("token");

        if (!storedToken) {
            navigate("/");
        } else {
            setToken(storedToken);
        }
    }, [navigate]);

    const fetchEmployeeDetails = async () => {
        if (!selectedOutlet || !token) {
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${baseUrl}/employee-analytics`, {
                token: token,
                startDate: startDate,
                endDate: endDate,
                outlet: selectedOutlet,
            });

            const data = response.data || [];
            data.sort((a, b) => b.Total_Sales - a.Total_Sales);
            setEmployeeData(data);
        } catch (error) {
            toast.error("Failed to fetch employee data");
            console.error("Error fetching employee data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedOutlet && token) {
            fetchEmployeeDetails();
        }
    }, [selectedOutlet, token]);

    const handleFetchData = async () => {
        if (!selectedOutlet) {
            toast.error("Please select an outlet");
            return;
        }
        if (!startDate || !endDate) {
            toast.error("Please select both dates");
            return;
        }

        await fetchEmployeeDetails();
    };

    const handleIndividualDetails = (employee) => {
        navigate('/employeedetails', {
            state: {
                startdate: startDate,
                enddate: endDate,
                employee_name: employee.Employee,
                outlet_name: selectedOutlet
            }
        });
    }

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow">
                <div className="max-w-6xl p-2 mt-5 mx-auto">
                    <div className="flex justify-between px-12">
                        <div className="flex flex-col">
                            <label htmlFor="startDate" className="text-md font-semibold">Start Date: </label>
                            <input
                                type="date"
                                id="startDate"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                style={{ margin: "0" }}
                            />
                        </div>

                        <div className="flex justify-center text-center">
                            <SelectSearchInput
                                token={token}
                                setToken={setToken}
                                selectedOutlet={selectedOutlet}
                                setSelectedOutlet={setSelectedOutlet}
                            />
                        </div>

                        <div className="flex flex-col">
                            <label htmlFor="endDate" className="text-md font-semibold">End Date: </label>
                            <input
                                type="date"
                                id="endDate"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                style={{ margin: "0" }}
                            />
                        </div>
                    </div>

                    <div className="flex justify-center items-center mb-5">
                        <button
                            className="border-2 border-slate-300 hover:bg-slate-100 bg-slate-700 text-zinc-100 hover:text-zinc-700 rounded-lg flex items-center justify-center px-4 py-2"
                            onClick={handleFetchData}
                            disabled={loading}
                        >
                            {loading ? "Loading..." : "Search"}
                        </button>
                    </div>

                    <EmployeeTable employeeData={employeeData} loading={loading} handleIndividualDetails={handleIndividualDetails} />

                    <EmployeeSalesBarChart employeeData={employeeData} />

                    <h3 className="text-xl mt-5">Items Sold</h3>

                    <ItemSoldTable data={employeeData} />
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default EmployeeAnalysis;